import SkywayAdapter from '@/modules/webrtc/SkywayAdapter';
import AgoraAdapter from '@/modules/webrtc/agora/Adapter';
import ChimeAdapter from '@/modules/webrtc/ChimeAdapter';
import { store } from '@/utils/common'
import logger from '@/modules/Logger';
import UAParser from 'ua-parser-js';
import { buildJoinMeta, joinPrefix } from '@/modules/webrtc/joinLogUtils';

class RtcClient {
  constructor(Option) {
    this.provider = Option.provider;
    this.userId = Option.userId || null;
    this.remoteUsers = Option.remoteUsers;
    this.localTracks = Option.localTracks;
    this.adapter = null;
    this.adapterCredential = Option.credential;
    this.initError = false;
    this.videoProfile = Option.videoProfile;
    this.screenShareProfile = Option.screenShareProfile;
    this.enableAudio = Option.enableAudio;
    this.enableVideo = Option.enableVideo;
    this.socket = Option.socket;
    this.cameraId = Option.cameraId;
    this.micId = Option.micId;
    this.speakerId = Option.speakerId;
    this.virtualBgModel = Option.virtualBgModel;
    this.videoEffect = Option.videoEffect;
    this.remoteExternalId = Option.remoteExternalId;
    this.lessonId = Option.lessonId;

    const adapterParam = {
      userId: this.userId,
      credential: this.adapterCredential,
      remoteUsers: this.remoteUsers,
      videoProfile: this.videoProfile,
      screenShareProfile: this.screenShareProfile,
      enableAudio: this.enableAudio,
      enableVideo: this.enableVideo,
      socket: this.socket,
      videoDevice: this.cameraId,
      audioDevice: this.micId,
      audioOutputDevice: this.speakerId,
      virtualBgModel: this.virtualBgModel,
      videoEffect: this.videoEffect,
      remoteUserId: Option.remoteUserId,
      remoteExternalId: Option.remoteExternalId,
      lessonId: Option.lessonId
    };

    // init adapter by provider
    switch (this.provider) {
      case 'skyway':
        this.adapter = new SkywayAdapter(adapterParam);
        // add on event for socket connection
        this.adapter.setOnEvent();
        if (this.adapter.peer) {
          this.adapter.peer.on('error', error => {
            this.handleError(error);
          });
        }
        break;
      case 'agora':
        this.adapter = new AgoraAdapter(adapterParam);
        // add on event for socket connection
        this.adapter.setOnEvent();
        break;
      case 'awschime':
        if (this.adapterCredential.message && this.adapterCredential.message.length) {
          logger.info('[AWS CHIME] Adapter message - ' + this.adapterCredential.message.join('.'));
        }

        this.adapter = new ChimeAdapter(adapterParam);
        break;
      default:
        logger.info(this.provider ? 'Invalid WebRTC provider: ' + this.provider : 'WebRTC provider is not set.');
        this.initError = true;
        return;
    }
  }

  // join room
  async join (lessonId) {
    const maxRetries = 3;
    const totalAttempts = maxRetries + 1; // 4 total attempts
    const joinStartTime = Date.now();
    const isAgora = this.provider === 'agora';

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      if (isAgora) {
        logger.info(`${joinPrefix('RtcClient')} Join attempt ${attempt + 1}/${totalAttempts} - provider: ${this.provider}, lessonId: ${lessonId}`, buildJoinMeta({ provider: this.provider, lessonId }));
      }

      try {
        const response = await this.adapter.join(lessonId);
        logger.info(`[${this.provider}] join room: ` + lessonId, { operation: 'webrtc:join-room' });

        if (isAgora) {
          const elapsed = Date.now() - joinStartTime;
          logger.info(`${joinPrefix('RtcClient')} Join succeeded in ${elapsed}ms`, buildJoinMeta({ provider: this.provider, lessonId }));
        }

        return response;
      } catch (error) {
        const isRetryable = attempt < maxRetries
          && this.adapter.isNetworkError
          && this.adapter.isNetworkError(error);

        if (!isRetryable) {
          if (isAgora) {
            const elapsed = Date.now() - joinStartTime;
            logger.error(`${joinPrefix('RtcClient')} All retries exhausted - error: ${error.message || error}, lessonId: ${lessonId}, elapsed: ${elapsed}ms`, buildJoinMeta({ provider: this.provider, lessonId, extra: { event_type: 65 } }));
          } else {
            logger.error(`[${this.provider}] Error on join room: ` + lessonId, { operation: 'webrtc:join-room', payload: error.message || error, event_type: 65, lesson_id: lessonId });
          }

          return { error };
        }

        if (isAgora) {
          logger.warn(`${joinPrefix('RtcClient')} Retryable error, retrying in ${Math.pow(2, attempt) * 1000}ms (attempt ${attempt + 1}/${totalAttempts})`, buildJoinMeta({ provider: this.provider, lessonId }));
        } else {
          logger.info(`[${this.provider}] Retryable network error (code: ${error.code}), retrying in ${Math.pow(2, attempt) * 1000}ms (attempt ${attempt + 1}/${maxRetries})`, { operation: 'webrtc:join-room', lesson_id: lessonId });
        }

        const delay = Math.pow(2, attempt) * 1000; // 1s → 2s → 4s

        if (this.adapter.reset) {
          await this.adapter.reset();
        }

        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }

  // share screen
  async shareScreen (mode) {
    return await this.adapter.shareScreen(mode);
  }

  // enable/disable camera
  async toggleCamera (state) {
    return await this.adapter.toggleCamera(state);
  }

  // enable/disable microphone
  toggleMic (state) {
    this.adapter.toggleMic(state);
  }

  // disconnect
  async disconnect () {
    if (this.adapter) {
      return await this.adapter.disconnect();
    }
  }

  updateVideoTiles () {
    if (this.provider === 'awschime') {
      this.adapter.rebindVideoTile();
    }
  }

  // bind existing video tiles to the current video elements
  bindVideoTiles () {
    if (this.provider === 'awschime' && this.adapter.audioVideo) {
      this.adapter.updateTiles();
    }
  }

  getAdapter() {
    return this.adapter;
  }

  errorsToReload(errorType) {
    let toReload = false;
    switch(errorType) {
      case 'unavailable-id':
        toReload = true;
        break;
      case 'socket-error':
        toReload = true;
        break;
      case 'room-error':
        toReload = true;
        break;
      case 'server-error':
        toReload = true;
        break;
      default:
        toReload = false;
    }

    return toReload;
  }

  handleError(error) {
    if (!error.type) {
      return false;
    }

    const rtcWasReloaded = store.getRtcWasReloaded();
    if (this.errorsToReload(error.type) && !rtcWasReloaded) {
      logger.error('[' + this.provider  + '] Reloading page to fix error ' + error.type, { operation: 'webrtc:reload',event_type:66,lesson_id:this.lessonId });
      store.setRtcWasReloaded(true);
      location.reload(true);
    } else {
      logger.error('[' + this.provider  + '] Page was previously reloaded automatically to fix an error, will not reload again.', { operation: 'webrtc:reload',event_type:67,lesson_id:this.lessonId });
    }

    return true;
  }

  sendBrowserNameToRemote() {
    this.socket.emit('signal', { type:'remote-browser', browser: new UAParser().getBrowser().name });
  }
}

export default RtcClient;
